import { AgenturStore } from '../agentur.store';
import { SessionStore } from '../session.store';
import { runInAction, observable, makeObservable, action, computed } from 'mobx';
import { AgenturModel, AgtType } from 'app/models/agentur.model';
import { AgtDigitalReportModel, AgtDigitalReportStore } from '../agt.digitalreport.store';
import { BaseUiStore } from './base.ui.store';
import { ISelectOpt } from 'app/components/common/Select';
import { formatBM } from 'app/utils';
import _ from 'lodash';

export class DigitalReportVM {
	agt: AgenturModel;
	@observable
	item: AgtDigitalReportModel;

	constructor(agt: AgenturModel, item?: AgtDigitalReportModel) {
		makeObservable(this);
		this.agt = agt;
		// Sort items by bm descending to get latest first
		this.item = item || new AgtDigitalReportModel({ vtnr_id: this.agt.agtId, bm: 0 });
	}

	// Get the most recent report data
	@computed
	get pos() {
		return this.item;
	}
}

export class AgtDigitalreportUiStore extends BaseUiStore<DigitalReportVM> {
	name = 'AgtDigitalreportUiStore';
	session: SessionStore;
	agenturStore: AgenturStore;
	agtDigitalReportStore: AgtDigitalReportStore;

	@observable
	bms: number[] = [];

	@observable
	minBmCurrent?: number;

	@observable
	maxBmCurrent?: number;

	constructor(session: SessionStore, agenturStore: AgenturStore, agtDigitalReportStore: AgtDigitalReportStore) {
		super();
		makeObservable(this);
		this.session = session;
		this.agenturStore = agenturStore;
		this.agtDigitalReportStore = agtDigitalReportStore;
	}

	@computed
	get maxBm() {
		if (this.maxBmCurrent) return this.maxBmCurrent;
		return _.max(this.bms);
	}

	@computed
	get minBm() {
		if (this.minBmCurrent) return this.minBmCurrent;
		return _.min(this.bms);
	}

	@computed
	get bmOpts(): ISelectOpt[] {
		return this.bms.map((bm) => {
			return {
				value: bm,
				label: formatBM(bm),
			};
		});
	}

	@action
	async _baseLoad() {
		const distinctBms = await this.agtDigitalReportStore.getDistinctBMs();

		runInAction(() => {
			this.bms = distinctBms;
			// Set default BM range to the most recent month
			if (this.bms.length > 0) {
				this.maxBmCurrent = this.bms[this.bms.length - 1];
				this.minBmCurrent = this.bms[this.bms.length - 1];
			}
		});
	}
	@action
	async loadByMaxBm() {
		await this.load();
		const maxBm = this.maxBm;
		return this.loadByBm(maxBm!);
	}

	@action
	async loadByBm(bm: number) {
		const cacheKey = `AgtDigitalreportUiStore.loadByBm${bm}`;

		return this.withCache(
			cacheKey,
			async () => {
				// Ensure base data is loaded
				await this.load();

				// Load data for the specified BM from analytics store
				const agts = await this.agenturStore.findAll();
				const filteredAgts = agts.filter((x) => x.agtType === AgtType.agt);
				const data = await this.agtDigitalReportStore.loadByBm(bm);

				const res: DigitalReportVM[] = [];
				filteredAgts.forEach((agt, index) => {
					const agtData = data.find((d) => d.agtId === agt.agtId);
					if (agtData) {
						const vm = new DigitalReportVM(agt, agtData);
						res.push(vm);
					}
				});
				runInAction(() => {
					this.items = res;
				});
				return res;
			},
			{
				cacheResult: true,
			},
		);
	}

	@action
	async loadMazHistoryLast12Months(agtId: number): Promise<
		{
			bm: string;
			bmRaw: number;
			mazBestand: number;
			kndBestand: number;
			mazNeu: number;
			kndNeu: number;
			anzKnd: number;
		}[]
	> {
		const cacheKey = `AgtDigitalreportUiStore.loadMazHistoryLast12Months${agtId}`;

		return this.withCache(
			cacheKey,
			async () => {
				// Ensure base data is loaded
				await this.load();

				// Calculate current BM (YYYYMM format)
				const now = new Date();
				const currentBm = now.getFullYear() * 100 + (now.getMonth() + 1);

				// Filter out future months
				const pastBms = this.bms.filter(bm => bm <= currentBm);

				// Get last 12 months of available past data
				const last12Bms = pastBms.slice(-12);

				// Load all data for these months
				const allData = await Promise.all(last12Bms.map((bm) => this.agtDigitalReportStore.loadByBm(bm)));

				// Extract data for the specific agency and format for chart
				const result = last12Bms.map((bm, index) => {
					const monthData = allData[index];
					const agtData = monthData.find((d) => d.agtId === agtId);

					return {
						bm: formatBM(bm),
						bmRaw: bm,
						mazBestand: agtData?.anzMaz1870 || 0,
						kndBestand: agtData?.anzKnd1870 || 0,
						mazNeu: agtData?.anzMazNg || 0,
						kndNeu: agtData?.anzKndNg || 0,
						anzKnd: agtData?.anzKnd || 0,
					};
				});

				return result;
			},
			{
				cacheResult: true,
			},
		);
	}

	@action
	async loadMazHistoryLast12MonthsAggregated(agtIds: number[]): Promise<
		{
			bm: string;
			bmRaw: number;
			mazBestand: number;
			kndBestand: number;
			mazNeu: number;
			kndNeu: number;
			anzKnd: number;
		}[]
	> {
		const cacheKey = `AgtDigitalreportUiStore.loadMazHistoryLast12MonthsAggregated_${agtIds.sort().join('_')}`;

		return this.withCache(
			cacheKey,
			async () => {
				// Ensure base data is loaded
				await this.load();

				// Calculate current BM (YYYYMM format)
				const now = new Date();
				const currentBm = now.getFullYear() * 100 + (now.getMonth() + 1);

				// Filter out future months
				const pastBms = this.bms.filter(bm => bm <= currentBm);

				// Get last 12 months of available past data
				const last12Bms = pastBms.slice(-12);

				// Load all data for these months
				const allData = await Promise.all(last12Bms.map((bm) => this.agtDigitalReportStore.loadByBm(bm)));

				// Aggregate data across all specified agencies
				const result = last12Bms.map((bm, index) => {
					const monthData = allData[index];

					// Filter to only the specified agencies and aggregate
					let totalMazBestand = 0;
					let totalKndBestand = 0;
					let totalMazNeu = 0;
					let totalKndNeu = 0;
					let totalAnzKnd = 0;

					agtIds.forEach((agtId) => {
						const agtData = monthData.find((d) => d.agtId === agtId);
						if (agtData) {
							totalMazBestand += agtData.anzMaz1870 || 0;
							totalKndBestand += agtData.anzKnd1870 || 0;
							totalMazNeu += agtData.anzMazNg || 0;
							totalKndNeu += agtData.anzKndNg || 0;
							totalAnzKnd += agtData.anzKnd || 0;
						}
					});

					return {
						bm: formatBM(bm),
						bmRaw: bm,
						mazBestand: totalMazBestand,
						kndBestand: totalKndBestand,
						mazNeu: totalMazNeu,
						kndNeu: totalKndNeu,
						anzKnd: totalAnzKnd,
					};
				});

				return result;
			},
			{
				cacheResult: true,
			},
		);
	}
}
